import React, {
  useCallback,
  useRef,
  CSSProperties,
  MutableRefObject,
  useState,
  useMemo,
  Ref,
} from 'react';

import {
  useFloating,
  flip,
  shift,
  offset,
  hide,
  arrow,
  HideOptions,
  useHover,
  useInteractions,
  Placement,
  OffsetOptions,
  safePolygon,
  useFocus,
  useClick,
  useDismiss,
  autoUpdate,
  ShiftOptions,
  FlipOptions,
  useRole,
} from '@floating-ui/react';

import {
  omitKeys,
  useControlled,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import theme, { StylableComponent } from '@adsk/alloy-react-theme';

import Tooltip from './Tooltip';
import { Trigger, TriggerEvent, Variant } from './types';

export type UseTooltipProps = {
  /* Tooltip position */
  placement?: Placement;
  /* Override the default show/hide logic */
  show?: boolean;
  /* Whether the tooltip should show from hovering or clicking */
  trigger?: TriggerEvent;
  /* Whether the tooltip should close when clicked outside when is has a click trigger */
  enableCloseOnClickOutside: boolean;
  /* Callback to be called on hide */
  onHide?: () => void;
  /* Callback to be called on show */
  onShow?: () => void;
  /* Delay before show in milliseconds */
  delayShow?: number;
  /* Delay before hide in milliseconds */
  delayHide?: number;
  /* Set this to "TOOLTIP" to keep tooltip open when cursor moves to tooltip  */
  triggerTarget?: Trigger;
  /* Options that modify how overlays can hide (when target is hidden) */
  hideArgs?: HideOptions;
  /* Options that modify how the tooltip overlay can flip (see floating-ui documentation https://floating-ui.com/docs/flip) */
  flipArgs?: FlipOptions;
  /* Options to floating-ui's offset middleware which positions the tooltip */
  offset?: OffsetOptions;
  /* Options that modify how the tooltip overlay can shift (see floating-ui documentation https://floating-ui.com/docs/shift) */
  shiftArgs?: ShiftOptions;
  /* target element ref (get/set).  Prefer to use the ref returned from `getReferenceProps`  */
  targetRef?: Ref<HTMLElement>;
  /* overlay ref to overlay dom element.  Prefer to use the ref returned from `getFloatingProps` */
  overlayRef?: MutableRefObject<HTMLElement | null>;
  /* Tooltip variant */
  variant: Variant;
};

function useTooltip({
  enableCloseOnClickOutside,
  placement: placementProp,
  show: showProp,
  trigger,
  triggerTarget,
  delayShow,
  delayHide,
  onHide,
  onShow,
  targetRef,
  overlayRef,
  offset: offsetProp,
  variant,
  hideArgs = { strategy: 'referenceHidden' },
}: UseTooltipProps) {
  const isClickControlled = trigger === Tooltip.TRIGGER_EVENTS.CLICK;
  const interactive = triggerTarget === Tooltip.TRIGGER_TARGETS.TOOLTIP;

  const mutableTargetRef = useForwardedRef(targetRef || null);

  const arrowRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useControlled(false, showProp);
  const [referenceElement, setReferenceElement] = useState(
    mutableTargetRef?.current,
  );
  const [floatingElement, setFloatingElement] = useState(overlayRef?.current);

  const { context, placement, middlewareData, floatingStyles } = useFloating({
    elements: {
      reference: referenceElement,
      floating: floatingElement,
    },
    placement: placementProp,
    open: isOpen,
    onOpenChange: (open) => {
      if (open) {
        onShow?.();
      } else {
        onHide?.();
      }

      setIsOpen(open);
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetProp),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
      hide(hideArgs),
    ],
  });

  const focus = useFocus(context, {
    enabled: !isClickControlled,
  });

  const hover = useHover(context, {
    enabled: !isClickControlled,
    delay: { open: delayShow, close: delayHide },
    handleClose: interactive ? safePolygon({}) : undefined,
  });

  const click = useClick(context, { enabled: isClickControlled });

  const dismiss = useDismiss(context, {
    outsidePress: enableCloseOnClickOutside,
  });

  const role = useRole(context, {
    role: 'tooltip',
  });

  const interactions = useInteractions([focus, hover, click, dismiss, role]);

  const referenceRef = useCallback(
    (node: HTMLElement) => {
      if (mutableTargetRef) {
        mutableTargetRef.current = node;
      }
      setReferenceElement(node);
    },
    [mutableTargetRef],
  );

  const floatingRef = useCallback(
    (node: HTMLElement) => {
      if (overlayRef) {
        overlayRef.current = node;
      }
      setFloatingElement(node);
    },
    [overlayRef],
  );

  const getReferenceProps = useCallback<typeof interactions.getReferenceProps>(
    (userProps) => ({
      ...interactions.getReferenceProps({
        tabIndex: 0,
        ...userProps,
        ref: referenceRef,
      }),
    }),
    [interactions, referenceRef],
  );

  const allFloatingStyle = useMemo(
    () => ({
      ...floatingStyles,
      visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
    }),
    [floatingStyles, middlewareData.hide?.referenceHidden],
  );

  const getFloatingProps = useCallback<typeof interactions.getFloatingProps>(
    (userProps) =>
      interactions.getFloatingProps({
        ...userProps,
        ref: floatingRef,
        style: Object.assign(allFloatingStyle, userProps?.style),
        onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          event.stopPropagation();
          userProps?.onClick?.(event);
        },
      }),
    [floatingRef, allFloatingStyle, interactions],
  );

  const arrowTransform: CSSProperties['transform'] = `translate(${Math.round(
    middlewareData.arrow?.x || 0,
  )}px,${Math.round(middlewareData.arrow?.y || 0)}px)`;

  const arrowThemeVariants = {
    dark: theme.tokens.colors.background.inverse.subtle.value,
    medium: theme.tokens.colors.background.inverse.selected.value,
    light: theme.tokens.colors.text.inverse.value,
  };

  const arrowTheme = arrowThemeVariants[variant || 'dark'];

  const getArrowProps = useCallback(
    (userProps?: StylableComponent<Element>) => ({
      ref: arrowRef,
      placement,
      size: `5px`,
      color: arrowTheme,
      style: [
        {
          transform: arrowTransform,
        },
        userProps?.style,
      ],
      ...omitKeys(userProps, ['style']),
    }),
    [arrowTheme, arrowTransform, placement],
  );

  return {
    show: isOpen,
    placement,
    interactive,
    getReferenceProps,
    getFloatingProps,
    getArrowProps,
  };
}

export default useTooltip;
